// src/components/pv/ModuleParameterFields.jsx
//
// Spec-driven entry for a module's model coefficients.
//
// Why this exists
// ---------------
// The engine does not have "a" module model: it picks SAPM, CEC, De Soto, PVsyst
// or PVWatts from whichever coefficients the module happens to carry (see the DC
// cascade in PV_output/system_simulation.py, and the Knowledge tab, which already
// tells users this). The Add-custom-module form, however, asked only for
// datasheet quantities and fitted them to CEC — so every custom module became CEC
// and anyone holding a manufacturer or lab coefficient set had nowhere to put it.
//
// The backend already accepted raw coefficients (the catalogue writer stores what
// it receives when datasheet fields are absent); only the form blocked it.
//
// The field lists come from GET /catalog/parameter-sets rather than being repeated
// here, because a list that drifts from pv_core.parameter_sets would let users
// save a module the engine then refuses to run with
// "Could not infer DC model from module_parameters".
import { Alert, Box, Chip, Stack, TextField, Typography } from "@mui/material";

const isBlank = (v) => v === "" || v == null;

/** Required keys of a set that the current values do not satisfy. */
export function missingForSet(spec, values) {
  if (!spec) return [];
  return (spec.required || [])
    .map((f) => f.key)
    .filter((k) => isBlank(values?.[k]) || !Number.isFinite(Number(values[k])));
}

function FieldGrid({ fields, values, onChange, required }) {
  if (!fields?.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
        mb: 1.5,
      }}
    >
      {fields.map((f) => {
        const val = values?.[f.key] ?? "";
        const bad = required && (isBlank(val) || !Number.isFinite(Number(val)));
        return (
          <TextField
            key={f.key}
            size="small"
            label={`${f.label}${required ? " *" : ""}`}
            value={val}
            onChange={(e) => onChange(f.key, e.target.value)}
            error={bad && !isBlank(val)}
            helperText={f.unit ? `${f.key} · ${f.unit}` : f.key}
            InputLabelProps={{ shrink: true }}
          />
        );
      })}
    </Box>
  );
}

export default function ModuleParameterFields({ spec, values, onChange }) {
  if (!spec) return null;
  const missing = missingForSet(spec, values);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 0.75, flexWrap: "wrap" }}
        useFlexGap
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {spec.label}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={missing.length ? "warning" : "success"}
          label={
            spec.fitted
              ? `fitted to ${String(spec.resulting_model).toUpperCase()}`
              : `runs as ${String(spec.resulting_model).toUpperCase()}`
          }
          sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
        />
      </Stack>

      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
        {spec.help}
      </Typography>

      <FieldGrid fields={spec.required} values={values} onChange={onChange} required />

      {(spec.optional || []).length > 0 && (
        <>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
            Optional — improves fidelity where available.
          </Typography>
          <FieldGrid fields={spec.optional} values={values} onChange={onChange} />
        </>
      )}

      {missing.length > 0 && (
        <Alert severity="warning" sx={{ mt: 0.5 }}>
          <Typography variant="caption">
            Still needed: <b>{missing.join(", ")}</b>. A partial set matches no
            model, and the engine would reject the module at simulation time
            rather than on save.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
